/**
 * CorpFilterDrawer — Area type + device category + response time filter drawer.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import Drawer from '../../../../components/v2/Drawer.jsx';

const CorpFilterDrawer = ({
    open, onClose, onApply,
    areaTypeOptions, deviceCatOptions,
    currentAreaTypes, currentDeviceCats, currentResponseTime,
}) => {
    const [areaTypes, setAreaTypes] = useState(new Set());
    const [deviceCats, setDeviceCats] = useState(new Set());
    const [respEnabled, setRespEnabled] = useState(false);
    const [respOp, setRespOp] = useState('lt');
    const [respVal, setRespVal] = useState(60);
    const [atSearch, setAtSearch] = useState('');
    const [dcSearch, setDcSearch] = useState('');

    useEffect(() => {
        if (open) {
            setAreaTypes(new Set(currentAreaTypes || []));
            setDeviceCats(new Set(currentDeviceCats || []));
            const rt = currentResponseTime || [false, 'lt', 60];
            setRespEnabled(Boolean(rt[0]));
            setRespOp(rt[1] || 'lt');
            setRespVal(parseInt(rt[2], 10) || 60);
            setAtSearch('');
            setDcSearch('');
        }
    }, [open, currentAreaTypes, currentDeviceCats, currentResponseTime]);

    const toggle = (set, setter, id) => {
        const next = new Set(set);
        const key = String(id);
        if (next.has(key)) next.delete(key); else next.add(key);
        setter(next);
    };

    const handleApply = useCallback(() => {
        onApply({
            areaTypes: [...areaTypes].join(','),
            deviceCategories: [...deviceCats].join(','),
            responseTime: [respEnabled, respOp, respVal],
        });
    }, [areaTypes, deviceCats, respEnabled, respOp, respVal, onApply]);

    const handleClearAll = useCallback(() => {
        setAreaTypes(new Set());
        setDeviceCats(new Set());
        setRespEnabled(false);
        setRespOp('lt');
        setRespVal(60);
    }, []);

    const filteredAT = useMemo(() => {
        if (!atSearch) return areaTypeOptions || [];
        const q = atSearch.toLowerCase();
        return (areaTypeOptions || []).filter((at) => (at.area_type_name || '').toLowerCase().includes(q));
    }, [areaTypeOptions, atSearch]);

    const filteredDC = useMemo(() => {
        if (!dcSearch) return deviceCatOptions || [];
        const q = dcSearch.toLowerCase();
        return (deviceCatOptions || []).filter((dc) => (dc.device_category_short_name || '').toLowerCase().includes(q));
    }, [deviceCatOptions, dcSearch]);

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                title="Dashboard Filters"
                size="md"
                bodyProps={{ style: { overflowY: 'auto', flex: 1 } }}
                footer={(
                    <>
                        <button className="dp-btn dp-btn--cancel" onClick={handleClearAll} type="button">Clear All</button>
                        <button className="dp-btn dp-btn--primary" onClick={handleApply} type="button">Apply Filters</button>
                    </>
                )}
            >
                {/* Area Types */}
                <div className="ar-accordion">
                    <div className="ar-accordion__header ar-accordion__header--open">
                        <span className="ar-accordion__label">Area Types</span>
                        {areaTypes.size > 0 ? <span className="ar-accordion__count">{areaTypes.size} selected</span> : null}
                    </div>
                    <div className="ar-accordion__body">
                        <div className="ar-filter-section">
                            <div className="ar-filter-section__header">
                                <div className="ar-filter-section__actions">
                                    <button className="ar-filter-section__btn" onClick={() => setAreaTypes(new Set((areaTypeOptions || []).map((at) => String(at.area_type_id))))} type="button">Select All</button>
                                    <button className="ar-filter-section__btn" onClick={() => setAreaTypes(new Set())} type="button">Clear</button>
                                </div>
                            </div>
                            <input className="ar-filter-section__search" type="text" placeholder="Search area types..." value={atSearch} onChange={(e) => setAtSearch(e.target.value)} />
                            <div className="ar-filter-list">
                                {filteredAT.map((at) => {
                                    const id = String(at.area_type_id);
                                    return (
                                        <label key={id} className="ar-filter-list__item">
                                            <input type="checkbox" checked={areaTypes.has(id)} onChange={() => toggle(areaTypes, setAreaTypes, id)} />
                                            <span>{at.area_type_name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Device Categories */}
                <div className="ar-accordion">
                    <div className="ar-accordion__header ar-accordion__header--open">
                        <span className="ar-accordion__label">Device Categories</span>
                        {deviceCats.size > 0 ? <span className="ar-accordion__count">{deviceCats.size} selected</span> : null}
                    </div>
                    <div className="ar-accordion__body">
                        <div className="ar-filter-section">
                            <div className="ar-filter-section__header">
                                <div className="ar-filter-section__actions">
                                    <button className="ar-filter-section__btn" onClick={() => setDeviceCats(new Set((deviceCatOptions || []).map((dc) => String(dc.device_category_id))))} type="button">Select All</button>
                                    <button className="ar-filter-section__btn" onClick={() => setDeviceCats(new Set())} type="button">Clear</button>
                                </div>
                            </div>
                            <input className="ar-filter-section__search" type="text" placeholder="Search device categories..." value={dcSearch} onChange={(e) => setDcSearch(e.target.value)} />
                            <div className="ar-filter-list">
                                {filteredDC.map((dc) => {
                                    const id = String(dc.device_category_id);
                                    return (
                                        <label key={id} className="ar-filter-list__item">
                                            <input type="checkbox" checked={deviceCats.has(id)} onChange={() => toggle(deviceCats, setDeviceCats, id)} />
                                            <span>{dc.device_category_short_name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Response Time Filter */}
                <div className="ar-accordion">
                    <div className="ar-accordion__header ar-accordion__header--open">
                        <span className="ar-accordion__label">Response Time</span>
                        {respEnabled ? <span className="ar-accordion__count">Active</span> : null}
                    </div>
                    <div className="ar-accordion__body">
                        <div className="ar-filter-section">
                            <div className="ar-resp-filter">
                                <label className="ar-resp-filter__toggle">
                                    <input type="checkbox" checked={respEnabled} onChange={(e) => setRespEnabled(e.target.checked)} />
                                    <span>Enable</span>
                                </label>
                                {respEnabled ? (
                                    <div className="ar-resp-filter__controls">
                                        <select className="ar-resp-filter__select" value={respOp} onChange={(e) => setRespOp(e.target.value)}>
                                            <option value="lt">Less than</option>
                                            <option value="gt">Greater than</option>
                                            <option value="eq">Equal to</option>
                                        </select>
                                        <input className="ar-resp-filter__input" type="number" value={respVal} onChange={(e) => setRespVal(parseInt(e.target.value, 10) || 0)} min={0} max={3600} />
                                        <span className="ar-resp-filter__unit">min</span>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </Drawer>
        </>
    );
};

export default CorpFilterDrawer;
